import { useEffect, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { observer } from 'mobx-react'

import { OrderWrapper } from 'Modules/order/components/OrderWrapper/OrderWrapper'
import {
  AccessoriesStep,
  AddressStep,
  DeviceRentalStep,
  DeviceStep,
  SummaryStep,
} from 'Modules/order/components/Steps'
import {
  useOfferOrderMutation,
  useRedirectToConfirmationPage,
} from 'Modules/order/hooks'
import { OrderFlowTypesEnum } from 'Modules/order/types/orderFlow.types'
import { showErrorModal } from 'Modules/order/utils/errorModal'
import { notEmpty } from 'Modules/order/utils/notEmpty.utils'

import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'
import { Stepper, StepperStep } from 'Components/Stepper'

import { useStore } from 'Stores/index'

import { useActiveUser, useIsOrganisation, useModal } from 'Hooks'

import {
  mutationUpgradeDevice,
  mutationUpgradeDeviceVariables,
} from 'Constants/graphql/mutations/__generated__/mutationUpgradeDevice'
import { MUTATION_UPGRADE_DEVICE } from 'Constants/graphql/mutations/MutationUpgradeDevice'

import { OrderAddressType, OrderType } from 'Portal/__generated__/globalTypes'
import { productOffersDeviceItemPayloadMapper } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.mapper'
import { OrderDeviceResponseVariant } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.types'

export const OrderUpgrade = observer(() => {
  const { orderStore, userStore, rentalStore, navigationStore } = useStore()

  const {
    setMutationError,
    setOrderedDevices,
    chosenAccessories,
    remarks,
    chosenShippingAddress,
    chosenShippingAddressLabel,
    chosenDevice,
    shouldSkipAccessories,
    orderDevices,
  } = orderStore
  const { user } = userStore
  const { fetchRentalDetails, rentalDetails } = rentalStore
  const { mainNavigationConfig } = navigationStore
  const history = useHistory()
  const isOrganisation = useIsOrganisation()
  const { setModal, hideModal } = useModal()

  const { activeOrganisation, activeEmployee } = useActiveUser()
  const { employeeId = '', rentalId } = useParams<{
    employeeId?: string
    rentalId: string
  }>()
  const activeEmployeeId = employeeId || activeEmployee?.id.toString()

  const { redirectToConfirmationPage } =
    useRedirectToConfirmationPage(activeEmployeeId)

  const shippingAddressOrderPayload = useMemo(
    () => ({
      city: chosenShippingAddress?.city || '',
      country: chosenShippingAddress?.country || '',
      name: chosenShippingAddress?.name || '',
      nameAddition: chosenShippingAddress?.nameAddition,
      street: chosenShippingAddress?.street || '',
      streetAddition: chosenShippingAddress?.streetAddition,
      type: OrderAddressType.shipping,
      zipcode: chosenShippingAddress?.zipcode || '',
    }),
    [
      chosenShippingAddress?.city,
      chosenShippingAddress?.country,
      chosenShippingAddress?.name,
      chosenShippingAddress?.nameAddition,
      chosenShippingAddress?.street,
      chosenShippingAddress?.streetAddition,
      chosenShippingAddress?.zipcode,
    ],
  )

  const { handleOfferOrderMutation, isOfferOrderLoading } =
    useOfferOrderMutation({
      handleOnCompletedOrder: () => {
        redirectToConfirmationPage()
      },
      variables: {
        input: {
          orders: [
            {
              employeeId: activeEmployeeId,
              items: productOffersDeviceItemPayloadMapper(
                chosenDevice,
                chosenAccessories.size
                  ? [...chosenAccessories].map(([_id, accessory]) => accessory)
                  : null,
              ),
              offerId: chosenDevice?.offerId || '',
              orderType: OrderType.upgrade,
              organisationId:
                activeEmployee?.organisation?.id.toString() ||
                activeOrganisation?.id.toString() ||
                '',
              previousRentalID: rentalDetails?.rental.id,
              remark: remarks,
              shippingAddress: shippingAddressOrderPayload,
            },
          ],
          requestingUserID: user.userId?.toString() ?? '0',
        },
      },
    })

  const [createUpgradeDeviceCaseItem, { loading: isMutationLoading }] =
    useMutation<mutationUpgradeDevice, mutationUpgradeDeviceVariables>(
      MUTATION_UPGRADE_DEVICE,
      {
        onCompleted: data => {
          const metadata = {
            orderedDevice: chosenDevice?.deviceDefinition.name,
            quantity: chosenDevice?.quantity,
            userType: isOrganisation ? 'Admin' : 'Employee',
          }

          Intercom('trackEvent', 'rental-complete-upgrade', metadata)

          if (data.upgradeDevice.caseItem.subCaseItems) {
            const filteredDevices =
              data.upgradeDevice.caseItem.subCaseItems?.filter(notEmpty)

            setOrderedDevices(filteredDevices)
          } else {
            setOrderedDevices(Number(data.upgradeDevice.caseItem.id))
          }

          setMutationError(false)
          redirectToConfirmationPage()
        },
        onError: () => {
          setMutationError(true)

          showErrorModal(() => {
            hideModal()
            history.push(mainNavigationConfig.url)
          }, setModal)
        },
        variables: {
          input: {
            accessories: chosenAccessories.size
              ? [...chosenAccessories].map(([_id, accessory]) => ({
                  customerLabel: accessory?.customerLabel ?? '',
                  id: accessory.deviceDefinition.id,
                  quantity: accessory.quantity,
                }))
              : null,
            comment: remarks,
            device: {
              customerLabel: chosenDevice?.customerLabel ?? '',
              id: chosenDevice?.deviceDefinition.id ?? '',
              quantity: chosenDevice?.quantity ?? 1,
            },
            employeeID: activeEmployeeId,
            organisationID:
              activeEmployee?.organisation?.id.toString() ||
              activeOrganisation?.id.toString() ||
              '',
            previousRentalID: rentalDetails?.rental.id ?? '',
            previousStocklistItemID: rentalDetails?.rental.stocklistItem?.id,
            rentCategoryID: chosenDevice?.rentCategoryId || '',
            requestingUserID: user.userId?.toString() || '',
            shippingAddress: {
              _label: chosenShippingAddressLabel,
              city: chosenShippingAddress?.city || '',
              country: chosenShippingAddress?.country || '',
              name: chosenShippingAddress?.name || '',
              nameAddition: chosenShippingAddress?.nameAddition,
              street: chosenShippingAddress?.street || '',
              streetAddition: chosenShippingAddress?.streetAddition,
              zipcode: chosenShippingAddress?.zipcode || '',
            },
          },
        },
      },
    )

  useEffect(() => {
    if (rentalDetails?.rental.id === rentalId) {
      return
    }

    fetchRentalDetails({
      isOrganisationView: isOrganisation,
      rentalID: rentalId,
    })
  }, [rentalId, fetchRentalDetails, isOrganisation, rentalDetails?.rental.id])

  const orderNewStepsConfig = useMemo(() => {
    const baseSteps: StepperStep[] = [
      {
        headingLabel: 'Order_DeviceRentalStep_heading',
        renderStepContent: ({ handleNextStep }) => (
          <DeviceRentalStep handleNextStep={handleNextStep} />
        ),
      },
      {
        headingLabel: 'NewOrder_choose_device_header',
        renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
          <DeviceStep
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
            isProlongingPossible={
              rentalDetails?.rental.actions.showDeviceProlongingAction ?? false
            }
          />
        ),
        subHeadingLabel: 'NewOrder_choose_device_subheader',
      },
      {
        headingLabel: 'NewOrder_shipping_address_header',
        renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
          <AddressStep
            activeEmployeeId={activeEmployeeId}
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
          />
        ),
        subHeadingLabel: 'NewOrder_shipping_address_subheader',
      },
      {
        headingLabel: 'NewOrder_order_summary_header',
        renderStepContent: ({ handleSetCurrentStep, handlePreviousStep }) => (
          <SummaryStep
            activeEmployeeId={activeEmployeeId}
            handleOrderSubmit={() => {
              if (
                orderDevices?.variant === OrderDeviceResponseVariant.CATALOG
              ) {
                handleOfferOrderMutation()
              } else {
                createUpgradeDeviceCaseItem()
              }
            }}
            handlePreviousStep={handlePreviousStep}
            isSubmitLoading={isMutationLoading || isOfferOrderLoading}
            setCurrentOrderStep={handleSetCurrentStep}
          />
        ),
        subHeadingLabel: 'NewOrder_order_summary_subheader',
      },
    ]

    const accessoriesStep: StepperStep = {
      headingLabel: 'NewOrder_choose_accessories_header',
      renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
        <AccessoriesStep
          handleNextStep={handleNextStep}
          handlePreviousStep={handlePreviousStep}
        />
      ),
      subHeadingLabel: 'NewOrder_choose_accessories_subheader',
    }

    if (shouldSkipAccessories) {
      return baseSteps
    }

    baseSteps.splice(2, 0, accessoriesStep)

    return baseSteps
  }, [
    activeEmployeeId,
    createUpgradeDeviceCaseItem,
    handleOfferOrderMutation,
    isMutationLoading,
    isOfferOrderLoading,
    orderDevices,
    rentalDetails,
    shouldSkipAccessories,
  ])

  if (!rentalDetails) {
    return <PageLoadingState />
  }

  return (
    <OrderWrapper
      employeeId={activeEmployeeId}
      orderFlowType={OrderFlowTypesEnum.UPGRADE}
      renderOrderContent={() => <Stepper steps={orderNewStepsConfig} />}
    />
  )
})
